<template>
    <div v-if="activeOrderDetail" class="edit-form">
        <fieldset class="mb-4">
            <legend>Order Details</legend>
            <div class="mb-3">
                <label for="template" class="form-label">Type</label>
                <select id="template" v-model="activeOrderDetail.template" @change="applyTemplate(activeOrderDetail)"
                    class="form-select">
                    <option value="">Choose a template</option>
                    <option value="phone">Phone</option>
                    <option value="name">Name</option>
                    <option value="email">Email</option>
                    <option value="tableTent">Table Tent</option>
                </select>
                <small class="text-muted">Select a template to auto-fill question details based on common
                    scenarios.</small>
            </div>
            <div class="mb-3">
                <label for="startOrder" class="form-label">Question Timing</label>
                <select id="timing" v-model="activeOrderDetail.timing" class="form-select">
                    <option value="startOrder">New Order</option>
                    <option value="beforeValidate">Before Checkout</option>
                </select>
            </div>
            <div class="mb-3">
                <label for="title" class="form-label">Ask</label>
                <input id="title" type="text" v-model="activeOrderDetail.title" class="form-control"
                    placeholder="Title" />
            </div>
            <div class="mb-3" v-if="!activeOrderDetail.orderProperty">
                <label for="posLabel" class="form-label">POS Label</label>
                <input id="posLabel" type="text" v-model="activeOrderDetail.posLabel" class="form-control"
                    placeholder="" />
            </div>
            <div class="mb-3">
                <label for="placeholder" class="form-label">Placeholder</label>
                <input id="placeholder" type="text" v-model="activeOrderDetail.placeholder" class="form-control"
                    placeholder="Placeholder" />
            </div>
            <div class="mb-3 form-check form-switch">
                <input id="required" type="checkbox" v-model="activeOrderDetail.required" class="form-check-input" />
                <label for="required" class="form-check-label">Required</label>
            </div>
        </fieldset>
        <button @click="toggleAdvanced" class="btn btn-secondary mb-3">
            {{ activeOrderDetail.showAdvanced ? 'Hide Advanced' : 'Show Advanced' }}</button>
        <div v-if="activeOrderDetail.showAdvanced" class="modal fade show" tabindex="-1"
            style="display: block; background: rgba(0, 0, 0, 0.5);">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Advanced Settings</h5>
                        <button type="button" class="btn-close" @click="toggleAdvanced"></button>
                    </div>
                    <div class="modal-body">
                        <div class="mb-3">
                            <label for="description" class="form-label">Instructions</label>
                            <textarea id="description" v-model="activeOrderDetail.text" class="form-control"
                                placeholder="Description"></textarea>
                        </div>
                        <div class="mb-3">
                            <label for="minLength" class="form-label">Minimum Length</label>
                            <input id="minLength" type="number" v-model="activeOrderDetail.minLength"
                                class="form-control" placeholder="Min Length" />
                        </div>
                        <div class="mb-3">
                            <label for="maxLength" class="form-label">Maximum Length</label>
                            <input id="maxLength" type="number" v-model="activeOrderDetail.maxLength"
                                class="form-control" placeholder="Max Length" />
                        </div>
                        <div class="mb-3">
                            <label for="regex" class="form-label">Validation Regex</label>
                            <input id="regex" type="text" v-model="activeOrderDetail.validationRegex"
                                class="form-control" placeholder="Regex" />
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="toggleAdvanced">Close</button>
                    </div>
                </div>
            </div>
        </div>
        <div class="text-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="questionCancel">Cancel</button>
            <button type="submit" class="btn btn-primary" @click="questionSave">Save Changes</button>
        </div>
    </div>
    <div v-else-if="activeMessage" class="edit-form">
        <fieldset class="mb-4">
            <legend>Message Details</legend>
            <div class="mb-3">
                <label for="messageType" class="form-label">Type</label>
                <select id="messageType" v-model="activeMessage.type" class="form-select">
                    <option value="banner">Banner</option>
                    <option value="dialog">Dialog</option>
                </select>
                <small class="text-muted">Banners are always visible at the top of the menu. Dialogs require user interaction to dismiss.</small>
            </div>
            <div class="mb-3" v-if="activeMessage.type === 'banner'">
                <label for="bannerType" class="form-label">Banner Style</label>
                <select id="bannerType" v-model="activeMessage.bannerType" class="form-select">
                    <option value="info">Info (Blue)</option>
                    <option value="success">Success (Green)</option>
                    <option value="warning">Warning (Yellow)</option>
                    <option value="danger">Danger (Red)</option>
                </select>
            </div>
            <div class="mb-3">
                <label for="messageTitle" class="form-label">Title</label>
                <input id="messageTitle" type="text" v-model="activeMessage.title" class="form-control"
                    placeholder="Message Title" maxlength="100" />
            </div>
            <div class="mb-3">
                <label for="messageText" class="form-label">Message</label>
                <textarea id="messageText" v-model="activeMessage.text" class="form-control"
                    placeholder="Message content" rows="3" maxlength="300"></textarea>
            </div>
            <div class="mb-3" v-if="activeMessage.type === 'dialog'">
                <label for="buttonText" class="form-label">Button Text</label>
                <input id="buttonText" type="text" v-model="activeMessage.buttonText" class="form-control"
                    placeholder="OK" maxlength="25" />
            </div>
            <div class="mb-3 form-check form-switch" v-if="activeMessage.type === 'dialog'">
                <input id="showCancel" type="checkbox" v-model="activeMessage.showCancel" class="form-check-input" />
                <label for="showCancel" class="form-check-label">Show Cancel Button</label>
            </div>
            <div class="mb-3" v-if="activeMessage.type === 'dialog' && activeMessage.showCancel">
                <label for="cancelText" class="form-label">Cancel Button Text</label>
                <input id="cancelText" type="text" v-model="activeMessage.cancelText" class="form-control"
                    placeholder="Cancel" />
            </div>
        </fieldset>
        <div class="text-end">
            <button type="button" class="btn btn-outline-secondary me-2" @click="messageCancel">Cancel</button>
            <button type="submit" class="btn btn-primary" @click="messageSave">Save Changes</button>
        </div>
    </div>
    <div v-else class="row">
        <form @submit.prevent="handleSubmit">
            <fieldset class="mb-4">
                <legend>General Settings</legend>
                <div class="row mb-3">
                    <div class="col-md-6">
                        <div class="form-check form-switch">
                            <input type="checkbox" class="form-check-input" id="active" v-model="form.active">
                            <label for="active" class="form-check-label">Active</label>
                        </div>
                    </div>
                    <div class="col-md-6" v-if="config.rails === 'olo'">
                        <label for="type" class="form-label">Fulfillment Type</label>
                        <select id="type" v-model="form.type" class="form-select">
                            <option value="DINE_IN">Dine In</option>
                            <option value="PICKUP">Pickup</option>
                            <option value="DELIVERY">Delivery</option>
                        </select>
                    </div>
                    <div class="col-md-6" v-if="config.rails === 'stm'">
                        <label for="type" class="form-label">SkyTab POS Order Type</label>
                        <select id="type" v-model="form.orderTypePosGuid" class="form-select">
                            <option v-for="orderType in orderTypes" :key="orderType.id" :value="orderType.id">{{
                                orderType.title }}</option>
                        </select>
                        <div class="border border-secondary mt-3 p-2 rounded">
                            <i class="bi bi-info-circle-fill me-2"></i>
                            <strong>Note:</strong> Only order types with <em>"Require Ticket Name"</em> and <em>"Require
                                Customer"</em> disabled in SkyTab will appear here.
                                <br />
                            <button type="button" class="btn btn-link p-0 mt-2" @click.prevent="refreshOrderTypes" :disabled="loading">
                                <i :class="loading ? 'bi bi-arrow-repeat spin' : 'bi bi-arrow-clockwise'"></i> Refresh
                            </button>
                        </div>
                    </div>
                </div>
                <div class="mb-3">
                    <label for="title" class="form-label">Display Name</label>
                    <input type="text" id="title" v-model="form.name" class="form-control"
                        :class="{ 'is-invalid': form.name.trim() === '' }" required>
                    <div v-if="form.name.trim() === ''" class="invalid-feedback">
                        Display name is required.
                    </div>
                </div>
                <div class="mb-3">
                    <label for="icon" class="form-label">Icon</label>
                    <div class="input-group">
                        <select id="icon" v-model="form.icon" class="form-select">
                            <option value="">No Icon</option>
                            <option v-for="icon in icons" :key="icon" :value="icon">{{ camelToTitle(icon) }}</option>
                        </select>
                        <span class="input-group-text">
                            <SvgIcon v-if="form.icon" :name="form.icon" width="2rem" height="2rem" />
                        </span>
                    </div>
                </div>
            </fieldset>
            <fieldset class="mb-4">
                <legend>Questions</legend>
                <p class="text-muted">Questions help collect information from customers to fulfill orders. One question
                    can be selected as the ticket name, making it easier to look up in the POS system.</p>
                <ul class="list-group">
                    <li v-for="(orderDetail, index) in form.orderDetails" :key="index"
                        class="list-group-item d-flex justify-content-between align-items-center"
                        :class="{ 'selected-ticket-name': form.ticketNameQuestion === orderDetail.id }">
                        <div class="d-flex align-items-center">
                            <input type="radio" :id="'ticketName' + index" :value="orderDetail.id"
                                v-model="form.ticketNameQuestion" class="me-2">
                            <label :for="'ticketName' + index" class="mb-0">
                                <strong>{{ orderDetail.title }}</strong>
                            </label>
                            <span v-if="orderDetail.required" class="badge bg-primary ms-2">Required</span>
                            <span v-if="form.ticketNameQuestion === orderDetail.id" class="badge bg-success ms-2">Ticket
                                Name</span>
                        </div>
                        <div>
                            <span class="btn btn-icon" @click="questionEdit(orderDetail)">
                                <SvgIcon name="edit" width="1.5rem" height="1.5rem" />
                            </span>
                            <span class="btn btn-icon" @click="questionDelete(index)">
                                <SvgIcon name="delete" width="1.5rem" height="1.5rem" />
                            </span>
                        </div>
                    </li>
                </ul>
                <button class="btn btn-primary mt-3 w-100" @click="questionNew">Add Question</button>
            </fieldset>
            <fieldset class="mb-4">
                <legend>Messages</legend>
                <p class="text-muted">Messages can be displayed as banners at the top of the menu or as dialog boxes. Banners are always visible, while dialogs require user interaction to dismiss.</p>
                <ul class="list-group">
                    <li v-for="(message, index) in form.messages" :key="index"
                        class="list-group-item d-flex justify-content-between align-items-center">
                        <div class="d-flex align-items-center">
                            <span :class="['badge', 'me-2', message.type === 'banner' ? 'bg-info' : 'bg-warning']">
                                {{ message.type === 'banner' ? 'Banner' : 'Dialog' }}
                            </span>
                            <strong>{{ message.title }}</strong>
                        </div>
                        <div>
                            <span class="btn btn-icon" @click="messageEdit(message)">
                                <SvgIcon name="edit" width="1.5rem" height="1.5rem" />
                            </span>
                            <span class="btn btn-icon" @click="messageDelete(index)">
                                <SvgIcon name="delete" width="1.5rem" height="1.5rem" />
                            </span>
                        </div>
                    </li>
                </ul>
                <button class="btn btn-primary mt-3 w-100" @click="messageNew">Add Message</button>
            </fieldset>
            <fieldset class="mb-4" v-if="config.rails === 'olo'">
                <legend>Confirmation Text Message</legend>
                <div class="form-check form-switch mb-3">
                    <input type="checkbox" class="form-check-input" id="smsConfirmationEnabled" v-model="form.smsConfirmationEnabled">
                    <label for="smsConfirmationEnabled" class="form-check-label">Send SMS confirmation on POS confirm</label>
                </div>
                <div v-if="form.smsConfirmationEnabled" class="mb-3">
                    <label for="smsConfirmationMessage" class="form-label">Confirmation Message</label>
                    <textarea id="smsConfirmationMessage" v-model="form.smsConfirmationMessage" class="form-control"
                        placeholder="e.g. Your order is confirmed and will be ready in 15 minutes." rows="3" maxlength="255"></textarea>
                    <small class="text-muted">{{ form.smsConfirmationMessage ? form.smsConfirmationMessage.length : 0 }} / 255 characters</small>
                </div>
            </fieldset>
            <fieldset class="mb-4">
                <legend>Payment Settings</legend>
                <div class="mb-3 form-check form-switch">
                    <input type="checkbox" class="form-check-input" id="promptForTip" v-model="form.promptForTip">
                    <label for="promptForTip" class="form-check-label">Prompt for Tip</label>
                </div>
                <div v-if="config.rails === 'olo'" class="mb-3">
                    <label class="form-label">Allowed Payment Types</label>
                    <p class="text-muted small mb-3">Select which payment methods customers can use for this order type.</p>
                    <div class="row">
                        <div v-for="paymentType in paymentTypes" :key="paymentType.type" class="col-md-6 mb-3">
                            <div class="payment-type-card" :class="{ 'selected-payment-type': form.allowedPaymentTypes.includes(paymentType.type) }">
                                <label class="payment-type-label" @click="togglePaymentType(paymentType.type)">
                                    <div class="d-flex align-items-center">
                                        <input type="checkbox" class="form-check-input me-3" 
                                               :checked="form.allowedPaymentTypes.includes(paymentType.type)"
                                               @change="togglePaymentType(paymentType.type)">
                                        <div class="payment-type-content">
                                            <div class="d-flex align-items-center">
                                                <SvgIcon v-if="paymentType.icon" :name="paymentType.icon" width="1.5rem" height="1.5rem" class="me-2" />
                                                <span class="payment-type-name">{{ paymentType.name }}</span>
                                            </div>
                                            <small v-if="paymentType.instructions" class="text-muted payment-type-instructions">
                                                {{ paymentType.instructions }}
                                            </small>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div v-if="form.allowedPaymentTypes.length === 0" class="text-danger mt-2">
                        <i class="bi bi-exclamation-triangle me-1"></i>
                        At least one payment type must be selected.
                    </div>
                </div>
            </fieldset>
            <fieldset class="mb-4" v-if="config.rails === 'olo'">
                <legend>Price Settings</legend>
                <div class="mb-3">
                    <label for="priceAdjust" class="form-label">Item/Modifier Price Adjust</label>
                    <div class="input-group">
                        <span class="input-group-text">+</span>
                        <input type="number" v-model="form.priceAdjustAmount" class="form-control" placeholder="0"
                            min="0" max="100" step="0.01" />
                        <span class="input-group-text">%</span>
                    </div>
                    <small class="text-muted">Enter a percentage between 0 and 100 to increase item prices.</small>
                </div>
            </fieldset>
            <fieldset class="mb-4" v-if="form.view === 'retail'">
                <legend>Retail Settings</legend>
                <div class="form-check form-switch">
                    <input id="groceryCheckoutMode" type="checkbox" v-model="form.groceryCheckoutMode" class="form-check-input" />
                    <label for="groceryCheckoutMode" class="form-check-label">Grocery checkout mode (hide item list by default)</label>
                </div>
            </fieldset>
            <fieldset class="mb-4" v-if="isAdmin">
                <legend>Admin Settings</legend>
                <div class="row mb-3">
                    <div class="col-md-6">
                        <label for="mode" class="form-label">Mode</label>
                        <select id="mode" v-model="form.view" class="form-select">
                            <option value="restaurant">Restaurant</option>
                            <option value="retail">Retail</option>
                        </select>
                    </div>
                </div>
                <div class="mb-3" v-if="form.view === 'retail'">
                    <div class="form-check form-switch">
                        <input id="showSearchBar" type="checkbox" v-model="form.showSearchBar" class="form-check-input" />
                        <label for="showSearchBar" class="form-check-label">Show Search Bar</label>
                    </div>
                </div>
                <div class="mb-3 form-check form-switch">
                    <input id="barcodeScan" type="checkbox" v-model="form.barcodeScan" class="form-check-input" />
                    <label for="barcodeScan" class="form-check-label">Enable Barcode Scan</label>
                </div>
                <div class="mb-3 form-check form-switch">
                    <input id="promptCoupon" type="checkbox" v-model="form.promptCoupon" class="form-check-input" />
                    <label for="promptCoupon" class="form-check-label">Prompt for coupon before checkout</label>
                </div>
            </fieldset>

            <fieldset class="mb-4">
                <legend>Printing Settings</legend>
                <div class="mb-3 form-check form-switch">
                    <input id="printPickupChit" type="checkbox" v-model="form.printPickupChit" class="form-check-input" />
                    <label for="printPickupChit" class="form-check-label">Print Pickup Chit</label>
                    <small class="d-block text-muted">Automatically print a pickup chit with order number upon completion.</small>
                </div>
                <div class="mb-3 form-check form-switch" v-if="config.rails === 'olo'">
                    <input id="printConfirmationReceipt" type="checkbox" v-model="form.printConfirmationReceipt" class="form-check-input" />
                    <label for="printConfirmationReceipt" class="form-check-label">Print Confirmation Receipt</label>
                    <small class="d-block text-muted">Print a confirmation receipt at the processing POS terminal for each order from this flow.</small>
                </div>
            </fieldset>

            <fieldset class="mb-4">
                <legend>Order Restrictions</legend>
                <div class="mb-3">
                    <label for="maxItems" class="form-label">Maximum Items Per Order</label>
                    <div class="input-group">
                        <input 
                            type="number" 
                            id="maxItems" 
                            v-model="form.maxItems" 
                            class="form-control" 
                            placeholder="No restriction" 
                            min="0"
                            step="1"
                        />
                        <button 
                            type="button" 
                            class="btn btn-outline-secondary" 
                            @click="clearMaxItems"
                            :disabled="!form.maxItems"
                        >
                            Clear Restriction
                        </button>
                    </div>
                    <small class="text-muted">Leave empty or set to 0 for no restriction. Set a number to limit the maximum items customers can add to their order.</small>
                </div>
            </fieldset>

            <fieldset class="mb-4">
                <legend>Order Throttling</legend>
                <p class="text-muted">Limit the number of orders that can be placed in a 15-minute period. This includes completed orders and orders currently in progress.</p>
                <div class="mb-3 form-check form-switch">
                    <input class="form-check-input" type="checkbox" v-model="form.throttling.enabled" id="throttlingEnabled">
                    <label class="form-check-label" for="throttlingEnabled">Enable order throttling</label>
                </div>
                <div v-if="form.throttling.enabled" class="border-start border-3 ps-3 ms-1">
                    <div class="mb-3">
                         <label for="throttleMax" class="form-label">Max orders per 15 minute window</label>
                         <input type="number" class="form-control" id="throttleMax" min="1" v-model.number="form.throttling.maxOrdersPerWindow" :class="{ 'is-invalid': form.throttling.enabled && (!form.throttling.maxOrdersPerWindow || form.throttling.maxOrdersPerWindow < 1) }">
                         <div v-if="form.throttling.enabled && (!form.throttling.maxOrdersPerWindow || form.throttling.maxOrdersPerWindow < 1)" class="invalid-feedback">
                            Max orders must be at least 1 when throttling is enabled.
                        </div>
                    </div>
                    <div class="mb-3">
                        <label for="throttleWaitMessage" class="form-label">Wait Message</label>
                        <textarea class="form-control" id="throttleWaitMessage" v-model="form.throttling.waitMessage" rows="3" placeholder="Custom message shown to customers when throttled"></textarea>
                    </div>
                </div>
            </fieldset>
            <div class="text-end">
                <button type="button" class="btn btn-outline-secondary me-2" @click="cancel">Cancel</button>
                <button type="submit" class="btn btn-primary">Save Changes</button>
            </div>
        </form>
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import { mapState } from 'vuex';
import { toCamelCase } from '@/utils/helpers';
import { mapGetters } from 'vuex';
export default {
    props: {
        orderType: {
            type: Object,
            default: () => ({})
        },
        paymentTypes: {
            type: Array,
            default: () => []
        }
    },
    components: {
    },
    watch: {
        'form.ticketNameQuestion'() {
            this.setTicketNameQuestion(this.form.ticketNameQuestion);
        },
        'form.maxItems'(newVal) {
            console.log('OrderType watch - maxItems changed to:', newVal);
        },
        'form.priceAdjustAmount'() {
            this.adjustPrice();
        },
    },
    computed: {
        ...mapState('admin', ['config', 'orderTypes']),
        ...mapGetters('admin', ['isAdmin']),
        // All payment types are now available for selection
    },
    data() {
        return {
            form: {
                active: true,
                type: 'PICKUP',
                name: '',
                prepTime: '0',
                priceAdjustAmount: 0,
                priceAdjustType: 'percentUp',
                icon: '',
                view: 'restaurant',
                allowedPaymentTypes: ['CREDIT'],
                orderDetails: [],
                messages: [],
                isValidForm: true,
                orderTypePosGuid: null,
                ticketNameQuestion: null,
                barcodeScan: false,
                promptCoupon: false,
                printPickupChit: false,
                printConfirmationReceipt: true,
                showSearchBar: true,
                groceryCheckoutMode: false,
                maxItems: null,
                smsConfirmationEnabled: false,
                smsConfirmationMessage: '',
                throttling: {
                    enabled: false,
                    maxOrdersPerWindow: null,
                    windowMinutes: 15,
                    waitMessage: "Please wait before starting a new order. We're currently processing orders and will be ready shortly."
                }
            },
            icons: ['forHere', 'toGo', 'barcode', 'barcodeScanner', 'employee', 'user'],
            activeOrderDetail: null,
            activeMessage: null,
            originalMessage: null,
            templates: {
                phone: {
                    "title": "What is your Phone Number?",
                    "text": "So we can text you when the order is ready...",
                    "placeholder": "(123) 456-7890",
                    "required": false,
                    "minLength": null,
                    "useAsTicketName": false,
                    "maxLength": 30,
                    "inputType": "tel",
                    "posLabel": "Phone",
                    "orderProperty": "phone",
                    "timing": "startOrder",
                    "responseType": "tel",
                    "validationRegex": "^[0-9]{3}-[0-9]{3}-[0-9]{4}$"
                },
                name: {
                    "title": "What is your Name?",
                    "text": "",
                    "placeholder": "Mr. Smith",
                    "required": false,
                    "minLength": 2,
                    "useAsTicketName": false,
                    "maxLength": 30,
                    "inputType": "text",
                    "posLabel": "Phone",
                    "orderProperty": "name",
                    "timing": "startOrder",
                    "responseType": "text",
                    "validationRegex": "^[a-zA-Z ]{2,30}$"
                },
                email: {
                    "title": "What is your Email?",
                    "text": "",
                    "placeholder": "name@email.com",
                    "required": false,
                    "minLength": null,
                    "useAsTicketName": false,
                    "maxLength": 254,
                    "inputType": "email",
                    "orderProperty": "email",
                    "timing": "startOrder",
                    "responseType": "text",
                    "validationRegex": "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"
                },
                tableTent: {
                    "title": "Table Tent Number",
                    "text": "Grab a table tent and enter the number here so we can bring your order to you!",
                    "placeholder": "123",
                    "required": true,
                    "minLength": 3,
                    "useAsTicketName": false,
                    "maxLength": 3,
                    "inputType": "number",
                    "timing": "beforeValidate",
                    "posLabel": "Table Tent",
                    "responseType": "number",
                    "validationRegex": "^[0-9]{1,3}$"
                },
                advanced: {
                    "title": "",
                    "text": "",
                    "placeholder": "",
                    "required": false,
                    "minLength": 3,
                    "useAsTicketName": false,
                    "maxLength": 30,
                    "inputType": "text",
                    "timing": "startOrder",
                    "responseType": "text",
                    "validationRegex": null
                }
            },
            isValidDisplayName: true,
            isValidPaymentTypes: true,
            isValidOrderType: true,
            isValidFulfillmentType: true,
            loading: false
        };
    },
    methods: {
        toCamelCase,
        adjustPrice() {
            // Ensure the value is between 0 and 100
            const amount = parseFloat(this.form.priceAdjustAmount);
            if (amount < 0) {
                this.form.priceAdjustAmount = 0;
            } else if (amount > 100) {
                this.form.priceAdjustAmount = 100;
            }
            // Set the price adjust type to percentUp since we only allow increases
            this.form.priceAdjustType = 'percentUp';
        },
        validateForm() {
            // Check required fields
            this.isValidDisplayName = this.form.name.trim() !== '';
            this.isValidOrderType = this.config.rails === 'stm' ? this.form.orderTypePosGuid !== null : true;
            this.isValidFulfillmentType = this.config.rails === 'olo' ? this.form.type !== '' : true;
            this.isValidPaymentTypes = this.config.rails === 'olo' ? this.form.allowedPaymentTypes.length > 0 : true;

            // Update validation state
            this.isValidForm = this.isValidDisplayName && this.isValidOrderType && this.isValidFulfillmentType && this.isValidPaymentTypes;

            if (this.form.throttling.enabled) {
                const maxOrders = this.form.throttling.maxOrdersPerWindow;
                if (!maxOrders || maxOrders < 1) {
                    this.isValidForm = false;
                }
            }

            // Check each order detail if required
            this.form.orderDetails.forEach(detail => {
                detail.isValid = detail.title.trim() !== '';
            });

            return this.isValidForm && this.form.orderDetails.every(detail => detail.isValid);
        },
        handleSubmit() {
            if (this.validateForm()) {
                console.log('OrderType handleSubmit - form data being saved:', this.form);
                console.log('OrderType handleSubmit - maxItems value:', this.form.maxItems);
                this.$emit('save', this.form);
            } else {
                // Handle validation failure (e.g., show an error message)
                alert('Please correct the errors before submitting.');
            }
        },
        setTicketNameQuestion(selectedId) {
            this.form.orderDetails.forEach(detail => {
                detail.useAsTicketName = detail.id === selectedId;
            });
            // Update the model to reflect the current selection
            this.form.ticketNameQuestion = selectedId;
        },
        handlePreview() {

        },
        toggleAdvanced() {
            this.activeOrderDetail.showAdvanced = !this.activeOrderDetail.showAdvanced;
        },
        questionSave() {
            if (!this.activeOrderDetail.id) {
                this.activeOrderDetail.id = uuidv4();
            }

            if (this.activeOrderDetail.title.trim() !== '') {
                this.activeOrderDetail = null;
            } else {
                alert('The question title is required.');
            }
        },
        questionCancel() {
            if (this.activeOrderDetail && !this.activeOrderDetail.id) {
                const index = this.form.orderDetails.indexOf(this.activeOrderDetail);
                if (index > -1) {
                    this.form.orderDetails.splice(index, 1);
                }
            }
            this.activeOrderDetail = null;
        },
        questionEdit(detail) {
            this.activeOrderDetail = detail;
        },
        applyTemplate(detail) {
            Object.assign(detail, this.templates[detail.template]);
        },
        questionNew() {
            const newDetail = {
                title: '',
                text: '',
                placeholder: '',
                timing: 'startOrder',
                required: false,
                showAdvanced: false,
                orderProperty: '',
                posLabel: '',
                id: null
            };
            this.form.orderDetails.push(newDetail);
            this.questionEdit(newDetail);
        },
        isEdit(orderDetail) {
            return orderDetail === this.activeOrderDetail;
        },
        cancel() {
            this.$emit('close');
            this.resetForm();
        },
        camelToTitle(str) {
            return str.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
        },
        resetForm() {
            this.form = { active: true, type: '', name: '', prepTime: '5', icon: '', view: 'restaurant', allowedPaymentTypes: [], maxItems: null, smsConfirmationEnabled: false, smsConfirmationMessage: '' };
        },
        questionDelete(index) {
            if (confirm('Are you sure you want to delete this question?')) {
                this.form.orderDetails.splice(index, 1);
                this.activeOrderDetail = null;
            }
        },
        refreshOrderTypes() {
            this.loading = true;
            this.$store.dispatch('admin/fetchOrderTypes').finally(() => {
                this.loading = false;
            });
        },
        messageNew() {
            const newMessage = {
                id: null,
                type: 'banner',
                bannerType: 'info',
                title: '',
                text: '',
                buttonText: 'OK',
                showCancel: false,
                cancelText: 'Cancel'
            };
            this.form.messages.push(newMessage);
            this.messageEdit(newMessage);
        },
        messageEdit(message) {
            this.originalMessage = JSON.parse(JSON.stringify(message));
            this.activeMessage = message;
        },
        messageDelete(index) {
            if (confirm('Are you sure you want to delete this message?')) {
                this.form.messages.splice(index, 1);
                this.activeMessage = null;
                this.originalMessage = null;
            }
        },
        messageSave() {
            if (!this.activeMessage.id) {
                this.activeMessage.id = uuidv4();
            }

            if (this.activeMessage.title.trim() !== '') {
                this.activeMessage = null;
                this.originalMessage = null;
            } else {
                alert('The message title is required.');
            }
        },
        messageCancel() {
            if (this.activeMessage && !this.activeMessage.id) {
                const index = this.form.messages.indexOf(this.activeMessage);
                if (index > -1) {
                    this.form.messages.splice(index, 1);
                }
            } else if (this.activeMessage && this.originalMessage) {
                const index = this.form.messages.findIndex(m => m.id === this.activeMessage.id);
                if (index > -1) {
                    this.form.messages[index] = this.originalMessage;
                }
            }
            this.activeMessage = null;
            this.originalMessage = null;
        },
        clearMaxItems() {
            this.form.maxItems = null;
            console.log('OrderType clearMaxItems - maxItems cleared, new value:', this.form.maxItems);
        },
        togglePaymentType(paymentType) {
            const index = this.form.allowedPaymentTypes.indexOf(paymentType);
            if (index > -1) {
                this.form.allowedPaymentTypes.splice(index, 1);
            } else {
                this.form.allowedPaymentTypes.push(paymentType);
            }
        }
    },
    created() {
        const initialFormData = {
            showSearchBar: true,
            ...this.orderType
        };
        this.form = { ...this.form, ...initialFormData };
        if (this.orderType.allowedPaymentTypes) {
            this.form.allowedPaymentTypes = [...this.orderType.allowedPaymentTypes];
        }
        
        // Ensure maxItems is properly initialized
        if (this.orderType.maxItems !== undefined) {
            this.form.maxItems = this.orderType.maxItems;
        }

        if (this.orderType.throttling) {
            this.form.throttling = { ...this.form.throttling, ...this.orderType.throttling };
        }
        
        console.log('OrderType created - orderType:', this.orderType);
        console.log('OrderType created - form.maxItems:', this.form.maxItems);
    }
}
</script>
<style scoped>
h3 {
    margin-top: 2rem;
}

.list-group-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.25rem;
    border: 1px solid #ccc;
    margin-bottom: 0rem;
    border-top: 1px solid #ccc;
}

.list-group:first-child {
    /* only border radius on top */
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
}

.list-group:last-child {
    /* no border radius on top */
    border-bottom-left-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
}

.btn-icon {
    background: none;
    border: none;
    padding: 0;
    background: rgba(0, 0, 0, 0.04);
    opacity: 0.5;
}

.btn-icon:hover {
    opacity: 1;
}

.payment-type-card {
    border: 0.125rem solid #ddd;
    border-radius: 0.5rem;
    width: 100%;
    margin-bottom: 1rem;
    display: block;
    transition: all 0.2s ease;
}

.payment-type-card:hover {
    border-color: #007BFF;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.selected-payment-type {
    border-color: #007BFF;
    background-color: #E7F1FF;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.payment-type-label {
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    width: 100%;
    padding: 1rem;
    margin: 0;
}

.payment-type-content {
    flex: 1;
}

.payment-type-name {
    font-weight: 500;
    color: #333;
}

.payment-type-instructions {
    display: block;
    margin-top: 0.25rem;
    line-height: 1.3;
}

.form-check-input {
    margin-top: 0;
    position: static;
    opacity: 1;
}

.btn-group .btn-outline-primary.active {
    background-color: var(--primary-color) !important;
    color: var(--primary-standoff) !important;
}

.selected-ticket-name {
    background-color: #E7F1FF;
    border-color: #007BFF;
}

.spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
</style>
